import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Grow,
  Box,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { setLocationHoverIndex } from '../../../../../Helpers/dashboardSlice';
import { LOCATION_COLORS } from '../../../../../configs/colors';
import { NoRecords } from '../../../../../Helpers/Common';
import { useMediaQuery, useTheme } from '@mui/system';

const darkenColor = (color, amount = 0.2) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const newR = Math.max(0, Math.floor(r * (1 - amount)));
  const newG = Math.max(0, Math.floor(g * (1 - amount)));
  const newB = Math.max(0, Math.floor(b * (1 - amount)));

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '6px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        border: '1px solid #e5e7eb',
        borderRadius: '4px',
        zIndex: 9999,
        whiteSpace: 'nowrap',
        pointerEvents: 'none'
      }}>
        <Typography variant="caption" sx={{ fontWeight: 400, fontSize: '12px' }}>{`${payload[0].name} (${payload[0].payload.percentage}%)`}</Typography>
      </div>
    );
  }
  return null;
};

export default function LocationChart({ locationData, handleLocationClick }) {
  const dispatch = useDispatch();
  const locationHoverIndex = useSelector(state => state.dashboard.locationHoverIndex);
  const theme = useTheme();
  const isTab = useMediaQuery(theme.breakpoints.down('lg'));
  const isCustom = useMediaQuery('(min-width:1530px) and (max-width:1600px)')

  return (
    <Grow in={true} timeout={1300}>
      <Grid item xs={12} md={12} lg={6} xl={4}>
        <Grow
          in={true}
          style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
          timeout={2400}
        >
          <Card sx={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)", height: 380, display: 'flex', flexDirection: 'column', borderRadius: '2%' }}>
            <CardContent sx={{ flex: 1, position: 'relative' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', mt: 0 }}>
                Defect Concentration by Source
              </Typography>
              {locationData?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height="90%" style={{ marginTop: '0px' }}>
                    <PieChart>
                      <Pie
                        data={locationData}
                        cx={!isTab ? "40%" : "50%"}
                        cy="50%"
                        outerRadius={isCustom ? 100 : 120}
                        fill="#8884d8"
                        dataKey="value"
                        isAnimationActive={true}
                        animationDuration={500}
                        onMouseEnter={(_, index) => dispatch(setLocationHoverIndex(index))}
                        onMouseLeave={() => dispatch(setLocationHoverIndex(null))}
                        onClick={(_, index) => handleLocationClick(locationData[index])}
                        stroke="none"
                        style={{ cursor: 'pointer' }}
                      >
                        {locationData.map((entry, index) => {
                          const baseColor = LOCATION_COLORS[index % LOCATION_COLORS.length];
                          const fillColor = locationHoverIndex === index ? darkenColor(baseColor, 0.25) : baseColor;
                          return <Cell key={`cell-${index}`} fill={fillColor} stroke="none" />;
                        })}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 9999 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', alignItems: "flex-start", gap: 0.2, zIndex: 999, flexWrap: "wrap", textWrap: "", pointerEvents: 'none' }}>
                    {locationData?.slice(0, 10)?.map((entry, index) => (
                      <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: LOCATION_COLORS[index % LOCATION_COLORS.length], borderRadius: '2px', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ fontSize: '11px', color: 'rgba(0, 0, 0, 0.6)' }}>
                          {entry.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <NoRecords />
              )}
            </CardContent>
          </Card>
        </Grow>
      </Grid>
    </Grow>
  );
}
